/* eslint-disable @typescript-eslint/no-explicit-any */
import dynamic from 'next/dynamic';
import React from 'react';
import { BookingPriceDetailsProps } from '@/components/blocks/booking-price-details/booking-price-details';
import { CancellationInsuranceProps } from '@/components/blocks/cancellation-insurance/cancellation-insurance';
import { CustomerSummaryProps } from '@/components/blocks/customer-summary/customer-summary';
import { DetailedCustomerProps } from '@/components/blocks/detailed-customer/detailed-customer';
import { DetailedPassengerProps } from '@/components/blocks/detailed-passenger/detailed-passenger';
import { FlightSummaryProps } from '@/components/blocks/flight-summary/flight-summary';
import { GenericProductProps } from '@/components/blocks/generic-product/generic-product';
import { GiftCardProps } from '@/components/blocks/gift-card/gift-card';
import { GolfPackageProps } from '@/components/blocks/golf-package/golf-package';
import { HotelSummaryProps } from '@/components/blocks/hotel-summary/hotel-summary';
import { MultiGenericProductProps } from '@/components/blocks/multi-generic-product/multi-generic-product';
import { MultiSelectGenericProductProps } from '@/components/blocks/multi-select-generic-product/multi-select-generic-product';
import { MyTripBookingOverviewProps } from '@/components/blocks/my-trip-booking-overview/my-trip-booking-overview';
import { MyTripPassengerSummaryProps } from '@/components/blocks/my-trip-passenger-summary/my-trip-passenger-summary';
import { MyTripPaymentReceiptProps } from '@/components/blocks/my-trip-payment-receipt/my-trip-payment-receipt';
import { MyTripProductCarouselProps } from '@/components/blocks/my-trip-product-carousel/my-trip-product-carousel';
import { MyTripSurveyProps } from '@/components/blocks/my-trip-survey/my-trip-survey';
import { MyTripUpcomingPaymentProps } from '@/components/blocks/my-trip-upcoming-payment/my-trip-upcoming-payment';
import { MyTripWishlistContainerProps } from '@/components/blocks/my-trip-wishlist/my-trip-wishlist-container';
import { ParentProductProps } from '@/components/blocks/parent-product/parent-product';
import { PassengerSummaryProps } from '@/components/blocks/passenger-summary/passenger-summary';
import { PickupPointProps } from '@/components/blocks/pickup-point/pickup-point';
import { PreBookingPaymentProps } from '@/components/blocks/pre-booking-payment/pre-booking-payment';
import { ProfileNoLoyaltyWallContainerProps } from '@/components/blocks/profile-no-loyalty-wall/profile-no-loyalty-wall.container';
import { RoomSelectionProps } from '@/components/blocks/room-selection/room-selection';
import { SeatSelectionProps } from '@/components/blocks/seat-selection/seat-selection';
import { SimpleCustomerProps } from '@/components/blocks/simple-customer/simple-customer';
import { SimplePassengerProps } from '@/components/blocks/simple-passenger/simple-passenger';
import { TeeTimeProps } from '@/components/blocks/tee-time/tee-time';
import { TermsAndConditionsProps } from '@/components/blocks/terms-and-conditions/terms-and-conditions';
import { TravelInsuranceProps } from '@/components/blocks/travel-insurance/travel-insurance';
import { TravelSummaryProps } from '@/components/blocks/travel-summary/travel-summary';
import { TravelerAttributesContainerProps } from '@/components/blocks/traveler-attributes/traveler-attributes.container';
import { TripChecklistContainerProps } from '@/components/blocks/trip-checklist/trip-checklist.container';
import { BannerSliderProps } from '@/components/modules/banner-slider/banner-slider';
import BookingExtra from '@/components/modules/booking-extra/booking-extra';
import { ComposableContentProps } from '@/components/modules/composable-content/composable-content';

const ModuleLoadingFallback = () => <BookingExtra isLoading />;

const BannerSlider = dynamic(
  () => import('@/components/modules/banner-slider/banner-slider'),
  { loading: ModuleLoadingFallback },
);
const BookingPriceDetails = dynamic(
  () =>
    import('@/components/blocks/booking-price-details/booking-price-details'),
  { loading: ModuleLoadingFallback },
);
const CancellationInsurance = dynamic(
  () =>
    import('@/components/blocks/cancellation-insurance/cancellation-insurance'),
  { loading: ModuleLoadingFallback },
);
const ComposableContent = dynamic(
  () => import('@/components/modules/composable-content/composable-content'),
  { loading: ModuleLoadingFallback },
);
const CustomerSummary = dynamic(
  () => import('@/components/blocks/customer-summary/customer-summary'),
  { loading: ModuleLoadingFallback },
);
const DetailedCustomer = dynamic(
  () => import('@/components/blocks/detailed-customer/detailed-customer'),
  { loading: ModuleLoadingFallback },
);
const DetailedPassenger = dynamic(
  () => import('@/components/blocks/detailed-passenger/detailed-passenger'),
  { loading: ModuleLoadingFallback },
);
const FlightSummary = dynamic(
  () => import('@/components/blocks/flight-summary/flight-summary'),
  { loading: ModuleLoadingFallback },
);
const GenericProduct = dynamic(
  () => import('@/components/blocks/generic-product/generic-product'),
  { loading: ModuleLoadingFallback },
);
const GiftCard = dynamic(
  () => import('@/components/blocks/gift-card/gift-card'),
  { loading: ModuleLoadingFallback },
);
const GolfPackage = dynamic(
  () => import('@/components/blocks/golf-package/golf-package'),
  { loading: ModuleLoadingFallback },
);
const HotelSummary = dynamic(
  () => import('@/components/blocks/hotel-summary/hotel-summary'),
  { loading: ModuleLoadingFallback },
);
const MultiGenericProduct = dynamic(
  () =>
    import('@/components/blocks/multi-generic-product/multi-generic-product'),
  { loading: ModuleLoadingFallback },
);
const MultiSelectGenericProduct = dynamic(
  () =>
    import(
      '@/components/blocks/multi-select-generic-product/multi-select-generic-product'
    ),
  { loading: ModuleLoadingFallback },
);
const MyTripBookingOverview = dynamic(
  () =>
    import(
      '@/components/blocks/my-trip-booking-overview/my-trip-booking-overview'
    ),
  { loading: ModuleLoadingFallback },
);
const MyTripPassengerSummary = dynamic(
  () =>
    import(
      '@/components/blocks/my-trip-passenger-summary/my-trip-passenger-summary'
    ),
  { loading: ModuleLoadingFallback },
);
const MyTripPaymentReceipt = dynamic(
  () =>
    import(
      '@/components/blocks/my-trip-payment-receipt/my-trip-payment-receipt'
    ),
  { loading: ModuleLoadingFallback },
);
const MyTripProductCarousel = dynamic(
  () =>
    import(
      '@/components/blocks/my-trip-product-carousel/my-trip-product-carousel'
    ),
  { loading: ModuleLoadingFallback },
);
const MyTripSurvey = dynamic(
  () => import('@/components/blocks/my-trip-survey/my-trip-survey'),
  { loading: ModuleLoadingFallback },
);
const MyTripUpcomingPayment = dynamic(
  () =>
    import(
      '@/components/blocks/my-trip-upcoming-payment/my-trip-upcoming-payment'
    ),
  { loading: ModuleLoadingFallback },
);
const MyTripWishlist = dynamic(
  () =>
    import('@/components/blocks/my-trip-wishlist/my-trip-wishlist-container'),
  { loading: ModuleLoadingFallback },
);
const ParentProduct = dynamic(
  () => import('@/components/blocks/parent-product/parent-product'),
  { loading: ModuleLoadingFallback },
);
const PassengerSummary = dynamic(
  () => import('@/components/blocks/passenger-summary/passenger-summary'),
  { loading: ModuleLoadingFallback },
);
const PickupPoint = dynamic(
  () => import('@/components/blocks/pickup-point/pickup-point'),
  { loading: ModuleLoadingFallback },
);
const PreBookingPayment = dynamic(
  () => import('@/components/blocks/pre-booking-payment/pre-booking-payment'),
  { loading: ModuleLoadingFallback },
);
const ProfileNoLoyaltyWallContainer = dynamic(
  () =>
    import(
      '@/components/blocks/profile-no-loyalty-wall/profile-no-loyalty-wall.container'
    ),
);
const RoomSelection = dynamic(
  () => import('@/components/blocks/room-selection/room-selection'),
  { loading: ModuleLoadingFallback },
);
const SeatSelection = dynamic(
  () => import('@/components/blocks/seat-selection/seat-selection'),
  { loading: ModuleLoadingFallback },
);
const SimpleCustomer = dynamic(
  () => import('@/components/blocks/simple-customer/simple-customer'),
  { loading: ModuleLoadingFallback },
);
const SimplePassenger = dynamic(
  () => import('@/components/blocks/simple-passenger/simple-passenger'),
  { loading: ModuleLoadingFallback },
);
const TeeTime = dynamic(() => import('@/components/blocks/tee-time/tee-time'), {
  loading: ModuleLoadingFallback,
});
const TermsAndConditions = dynamic(
  () => import('@/components/blocks/terms-and-conditions/terms-and-conditions'),
  { loading: ModuleLoadingFallback },
);
const TravelerAttributesContainer = dynamic(
  () =>
    import(
      '@/components/blocks/traveler-attributes/traveler-attributes.container'
    ),
  { loading: ModuleLoadingFallback },
);
const TravelInsurance = dynamic(
  () => import('@/components/blocks/travel-insurance/travel-insurance'),
  { loading: ModuleLoadingFallback },
);
const TravelSummary = dynamic(
  () => import('@/components/blocks/travel-summary/travel-summary'),
  { loading: ModuleLoadingFallback },
);
const TripChecklistContainer = dynamic(
  () => import('@/components/blocks/trip-checklist/trip-checklist.container'),
  { loading: ModuleLoadingFallback },
);

export type BookingFlowModule =
  | ({
      $type: 'BannerSliderDto';
      stepAnchorLink?: string;
    } & BannerSliderProps)
  | ({
      $type: 'BookingPriceDetailsHeadlessModuleDto';
      stepAnchorLink?: string;
    } & BookingPriceDetailsProps)
  | ({
      $type: 'CancellationInsuranceHeadlessModuleDto';
      stepAnchorLink?: string;
    } & CancellationInsuranceProps)
  | ({
      $type: 'ComposableContentModuleDto';
      stepAnchorLink?: string;
    } & ComposableContentProps)
  | ({
      $type: 'CustomerSummaryHeadlessModuleDto';
      stepAnchorLink?: string;
    } & CustomerSummaryProps)
  | ({
      $type: 'DetailedCustomerHeadlessModuleDto';
      stepAnchorLink?: string;
    } & DetailedCustomerProps)
  | ({
      $type: 'DetailedPassengerHeadlessModuleDto';
      stepAnchorLink?: string;
    } & DetailedPassengerProps)
  | ({
      $type: 'FlightSummaryHeadlessModuleDto';
      stepAnchorLink?: string;
    } & FlightSummaryProps)
  | ({
      $type: 'GenericProductHeadlessModuleDto';
      stepAnchorLink?: string;
    } & GenericProductProps)
  | ({
      $type: 'GiftCardHeadlessModuleDto';
      stepAnchorLink?: string;
    } & GiftCardProps)
  | ({
      $type: 'GolfPackageHeadlessModuleDto';
      stepAnchorLink?: string;
    } & GolfPackageProps)
  | ({
      $type: 'HotelSummaryHeadlessModuleDto';
      stepAnchorLink?: string;
    } & HotelSummaryProps)
  | ({
      $type: 'MultiGenericProductHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MultiGenericProductProps)
  | ({
      $type: 'MyTripBookingOverviewHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MyTripBookingOverviewProps)
  | ({
      $type: 'MyTripPassengerSummaryHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MyTripPassengerSummaryProps)
  | ({
      $type: 'MyTripPaymentReceiptHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MyTripPaymentReceiptProps)
  | ({
      $type: 'MyTripProductCarouselHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MyTripProductCarouselProps)
  | ({
      $type: 'MyTripSurveyHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MyTripSurveyProps)
  | ({
      $type: 'MyTripUpcomingPaymentHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MyTripUpcomingPaymentProps)
  | ({
      $type: 'MyTripWishlistHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MyTripWishlistContainerProps)
  | ({
      $type: 'MultiSelectGenericProductHeadlessModuleDto';
      stepAnchorLink?: string;
    } & MultiSelectGenericProductProps)
  | ({
      $type: 'ParentProductHeadlessModuleDto';
      stepAnchorLink?: string;
    } & ParentProductProps)
  | ({
      $type: 'PassengerSummaryHeadlessModuleDto';
      stepAnchorLink?: string;
    } & PassengerSummaryProps)
  | ({
      $type: 'PickupPointHeadlessModuleDto';
      stepAnchorLink?: string;
    } & PickupPointProps)
  | ({
      $type: 'PreBookingPaymentHeadlessModuleDto';
      stepAnchorLink?: string;
    } & PreBookingPaymentProps)
  | ({
      $type: 'ProfileNoLoyaltyWallHeadlessModuleDto';
      stepAnchorLink?: string;
    } & ProfileNoLoyaltyWallContainerProps)
  | ({
      $type: 'RoomSelectionHeadlessModuleDto';
      stepAnchorLink?: string;
    } & RoomSelectionProps)
  | ({
      $type: 'SimpleCustomerHeadlessModuleDto';
      stepAnchorLink?: string;
    } & SimpleCustomerProps)
  | ({
      $type: 'SimplePassengerHeadlessModuleDto';
      stepAnchorLink?: string;
    } & SimplePassengerProps)
  | ({
      $type: 'TeeTimeHeadlessModuleDto';
      stepAnchorLink?: string;
    } & TeeTimeProps)
  | ({
      $type: 'TermsAndConditionsHeadlessModuleDto';
      stepAnchorLink?: string;
    } & TermsAndConditionsProps)
  | ({
      $type: 'MyTripTravelerAttributesHeadlessModuleDto';
      stepAnchorLink?: string;
    } & TravelerAttributesContainerProps)
  | ({
      $type: 'TravelInsuranceHeadlessModuleDto';
      stepAnchorLink?: string;
    } & TravelInsuranceProps)
  | ({
      $type: 'TravelSummaryHeadlessModuleDto';
      stepAnchorLink?: string;
    } & TravelSummaryProps)
  | ({
      $type: 'TransportSeatingHeadlessModuleDto';
      stepAnchorLink?: string;
    } & SeatSelectionProps)
  | ({
      $type: 'TripChecklistHeadlessModuleDto';
      stepAnchorLink?: string;
    } & TripChecklistContainerProps);

const BookingFlowModuleMap: {
  [key in BookingFlowModule['$type']]: React.ComponentType<any>;
} = {
  BannerSliderDto: BannerSlider,
  BookingPriceDetailsHeadlessModuleDto: BookingPriceDetails,
  CancellationInsuranceHeadlessModuleDto: CancellationInsurance,
  ComposableContentModuleDto: ComposableContent,
  CustomerSummaryHeadlessModuleDto: CustomerSummary,
  DetailedCustomerHeadlessModuleDto: DetailedCustomer,
  DetailedPassengerHeadlessModuleDto: DetailedPassenger,
  FlightSummaryHeadlessModuleDto: FlightSummary,
  GenericProductHeadlessModuleDto: GenericProduct,
  GiftCardHeadlessModuleDto: GiftCard,
  GolfPackageHeadlessModuleDto: GolfPackage,
  HotelSummaryHeadlessModuleDto: HotelSummary,
  MultiGenericProductHeadlessModuleDto: MultiGenericProduct,
  MultiSelectGenericProductHeadlessModuleDto: MultiSelectGenericProduct,
  MyTripBookingOverviewHeadlessModuleDto: MyTripBookingOverview,
  MyTripPassengerSummaryHeadlessModuleDto: MyTripPassengerSummary,
  MyTripPaymentReceiptHeadlessModuleDto: MyTripPaymentReceipt,
  MyTripProductCarouselHeadlessModuleDto: MyTripProductCarousel,
  MyTripSurveyHeadlessModuleDto: MyTripSurvey,
  MyTripTravelerAttributesHeadlessModuleDto: TravelerAttributesContainer,
  MyTripUpcomingPaymentHeadlessModuleDto: MyTripUpcomingPayment,
  MyTripWishlistHeadlessModuleDto: MyTripWishlist,
  ParentProductHeadlessModuleDto: ParentProduct,
  PassengerSummaryHeadlessModuleDto: PassengerSummary,
  PickupPointHeadlessModuleDto: PickupPoint,
  PreBookingPaymentHeadlessModuleDto: PreBookingPayment,
  ProfileNoLoyaltyWallHeadlessModuleDto: ProfileNoLoyaltyWallContainer,
  RoomSelectionHeadlessModuleDto: RoomSelection,
  SimpleCustomerHeadlessModuleDto: SimpleCustomer,
  SimplePassengerHeadlessModuleDto: SimplePassenger,
  TeeTimeHeadlessModuleDto: TeeTime,
  TermsAndConditionsHeadlessModuleDto: TermsAndConditions,
  TransportSeatingHeadlessModuleDto: SeatSelection,
  TravelInsuranceHeadlessModuleDto: TravelInsurance,
  TravelSummaryHeadlessModuleDto: TravelSummary,
  TripChecklistHeadlessModuleDto: TripChecklistContainer,
};

function getBookingFlowModule(
  type: BookingFlowModule['$type'],
  props: Omit<BookingFlowModule, '$type'>,
): {
  component: React.ComponentType<Omit<BookingFlowModule, '$type'>>;
  props: Omit<BookingFlowModule, '$type'>;
} {
  return { component: BookingFlowModuleMap[type], props };
}

export interface BookingFlowModuleListProps {
  /** An array of the modules to show */
  contentModules: BookingFlowModule[];
}

const BookingFlowModuleList = ({
  contentModules = [],
}: BookingFlowModuleListProps) => {
  return contentModules.map((module, moduleIndex) => {
    const { component: Component, props } = getBookingFlowModule(
      module.$type,
      module,
    );

    if (!Component) return null;

    return <Component key={moduleIndex} {...props} />;
  });
};

BookingFlowModuleList.displayName = 'BookingFlowModuleList';

export default BookingFlowModuleList;
